import {
  Anchor,
  Avatar,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Grid,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import profilePlaceholder from "../../assets/defaultProfile.png";
import { useGetPublicSpeakersInfinite } from "../../api/speakers";
import { useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBrandFacebook,
  IconBrandGithub,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandTwitter,
  IconBrandYoutube,
  IconLink,
  IconMail,
  IconWorld,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

const Speakers = ({ eventId }) => {
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);

  const [opened, { open, close }] = useDisclosure(false);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useGetPublicSpeakersInfinite({
      eventId,
      limit: 51,
      sort: { columnAccessor: "firstName", direction: "asc" },
    });

  const { t } = useTranslation();

  const speakers = data?.pages?.flatMap((page) => page.data);

  const handleViewSpeaker = (speakerId) => {
    setSelectedSpeaker(speakers.find((s) => s.id === speakerId));
    open();
  };

  const iconMap = {
    linkedin: <IconBrandLinkedin />,
    twitter: <IconBrandTwitter />,
    facebook: <IconBrandFacebook />,
    instagram: <IconBrandInstagram />,
    youtube: <IconBrandYoutube />,
    github: <IconBrandGithub />,
    email: <IconMail />,
    website: <IconWorld />,
  };

  if (isLoading) {
    return (
      <Container>
        <Loader color="blue" />
      </Container>
    );
  }

  const socialLinks = selectedSpeaker?.links?.filter((link) => {
    return link.type === "social";
  });

  const externalLinks = selectedSpeaker?.links?.filter(
    (link) => link.type === "external"
  );

  const getLinkProps = (link) => {
    const isEmail = link.platform === "email";

    return {
      href: isEmail ? `mailto:${link.url}` : link.url,
      target: isEmail ? undefined : "_blank",
      rel: isEmail ? undefined : "noopener noreferrer",
    };
  };

  if (speakers?.length === 0) {
    return (
      <Title
        order={2}
        mb="lg"
        style={{
          color: "#667C99",
          textAlign: "center",
        }}
      >
        {t("no_speakers_for_event")}
      </Title>
    );
  }
  return (
    <Grid gap="lg">
      {speakers?.map((speaker) => (
        <Grid.Col key={speaker.id} span={{ base: 12, sm: 6, md: 4 }}>
          <Card
            shadow="sm"
            padding="lg"
            radius="md"
            withBorder
            style={{
              cursor: "pointer",
            }}
            onClick={() => handleViewSpeaker(speaker.id)}
          >
            <Stack gap="md" align="center">
              <Avatar src={speaker.picture || profilePlaceholder} size={120} />
              <Box style={{ textAlign: "center" }}>
                <Title order={4}>
                  {speaker.firstName} {speaker.lastName}
                </Title>
                {speaker.occupation && (
                  <Text size="sm" c="dimmed" fw={500}>
                    {speaker.occupation}
                  </Text>
                )}
              </Box>
              <Text
                size="sm"
                lineClamp={3}
                style={{ textAlign: "center" }}
                mih={60}
              >
                {speaker.biography}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
      ))}
      <Modal
        opened={opened}
        onClose={close}
        title={
          <Title style={{ color: "#667C99" }}>{t("speaker_details")}</Title>
        }
      >
        <Stack gap="md">
          <Card key={selectedSpeaker?.id} padding="md" radius="md" withBorder>
            <Stack gap="md" align="center">
              <Group gap="md">
                <Avatar
                  src={selectedSpeaker?.picture || profilePlaceholder}
                  size={80}
                />
                <Box style={{ flex: 1 }}>
                  <Text fw={600} size="lg">
                    {selectedSpeaker?.firstName} {selectedSpeaker?.lastName}
                  </Text>

                  <Text size="sm" c="dimmed" fw={500}>
                    {selectedSpeaker?.occupation}
                  </Text>
                </Box>
                <Text size="sm" ta="justify">
                  {selectedSpeaker?.biography}
                </Text>
              </Group>
              {socialLinks && (
                <Flex gap="sm" justify="center">
                  {socialLinks.map((link) => (
                    <Anchor
                      component="a"
                      key={link.id}
                      c="dark"
                      {...getLinkProps(link)}
                    >
                      {iconMap[link.title?.toLowerCase()]}
                    </Anchor>
                  ))}
                </Flex>
              )}
              {externalLinks && (
                <Flex wrap="wrap" gap="10" justify="space-evenly">
                  {externalLinks.map((link) => {
                    return (
                      <Anchor
                        component="a"
                        size="sm"
                        key={link.id}
                        c="dark"
                        {...getLinkProps(link)}
                      >
                        <Group gap={3}>
                          <IconLink size={20} />
                          <Text>{link.title}</Text>
                        </Group>
                      </Anchor>
                    );
                  })}
                </Flex>
              )}
            </Stack>
          </Card>
        </Stack>
      </Modal>
      {isFetchingNextPage && (
        <Grid.Col span={12}>
          <Flex justify="center">
            <Loader color="blue" />
          </Flex>
        </Grid.Col>
      )}
      {hasNextPage && (
        <Grid.Col span={12}>
          <Flex justify="center">
            <Button
              variant="transparent"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage || !hasNextPage}
            >
              {isFetchingNextPage ? t("loading") : t("load_more")}
            </Button>
          </Flex>
        </Grid.Col>
      )}
    </Grid>
  );
};

export default Speakers;
